/**
 * App version — build-time информация о версии фронтенда.
 *
 * Значения подставляются Vite-ом на этапе сборки через `define`
 * (см. vite.config.ts → `__APP_VERSION__` и т.д.). В рантайме это уже
 * статичные строки, никакого `child_process`/`fs` в браузере.
 *
 * Источник версии (приоритет, разрешается в vite.config.ts):
 *   1. process.env.VITE_APP_VERSION  — CI прокидывает точный git tag/sha
 *      (см. PR-описание AK-274: deploy-staging.yml / deploy.yml build-step).
 *   2. `git describe --tags --always --dirty`  — локальная сборка с .git.
 *   3. package.json `version` + 'dev'  — fallback если git недоступен
 *      (например docker build без .git в контексте). Сборка НЕ падает.
 */

// Объявлены через Vite `define`. См. vite.config.ts.
declare const __APP_VERSION__: string;
declare const __APP_GIT_SHA__: string;
declare const __APP_GIT_BRANCH__: string;
declare const __APP_BUILD_TIME__: string;

export interface AppVersionInfo {
  /** Полная версия: git describe или package.version+'-dev'. */
  version: string;
  /** Короткий git sha (7 символов) или 'unknown'. */
  sha: string;
  /** Имя ветки на момент сборки или 'unknown'. */
  branch: string;
  /** ISO-время сборки. */
  buildTime: string;
  /** Окружение из Vite MODE: 'production' | 'development' | 'staging' | ... */
  env: string;
  /** Короткая строка для отображения: `v<version> · <sha>`. */
  label: string;
}

function safe(value: string | undefined, fallback: string): string {
  return value && value.length > 0 ? value : fallback;
}

const version = safe(
  typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : undefined,
  '0.0.0-dev',
);
const sha = safe(
  typeof __APP_GIT_SHA__ !== 'undefined' ? __APP_GIT_SHA__ : undefined,
  'unknown',
);
const branch = safe(
  typeof __APP_GIT_BRANCH__ !== 'undefined' ? __APP_GIT_BRANCH__ : undefined,
  'unknown',
);
const buildTime = safe(
  typeof __APP_BUILD_TIME__ !== 'undefined' ? __APP_BUILD_TIME__ : undefined,
  'unknown',
);

// import.meta.env.MODE: 'production' для prod-build, 'development' для dev.
// Можно переопределить через VITE_APP_ENV (CI staging/prod build).
const env = safe(
  import.meta.env.VITE_APP_ENV as string | undefined,
  import.meta.env.MODE,
);

/**
 * Нормализованный env-тег для отображения.
 * 'production' → 'prod', иначе как есть ('staging', 'dev').
 */
const envTag = env === 'production' ? 'prod' : env;

/**
 * Основной label для бейджа: 'v1.1049.0 · prod' (или 'v1.1049.0 · staging').
 *
 * Если version уже содержит sha (git describe формата 'v0.1.0-3-gabc1234')
 * то label = только version (sha не дублируем).
 *
 * Формат: `v{version} · {envTag}` (env маленьким, приглушённым — реализовано в компоненте).
 */
// compute-version.sh отдаёт version уже с ведущей 'v' (напр. 'v1.1054.0');
// fallback '0.0.0-dev' — без 'v'. Нормализуем к РОВНО одной ведущей 'v',
// иначе бейдж показывает 'vv1.1054.0' (двойная v).
const displayVersion = version.startsWith('v') ? version : `v${version}`;
const label = `${displayVersion} · ${envTag}`;

/** URL Confluence-летописи релизов (открывается по клику на бейдж). */
export const RELEASE_NOTES_URL =
  (import.meta.env.VITE_RELEASE_NOTES_URL as string | undefined) ||
  'https://humanless.atlassian.net/wiki/spaces/AIKolxoz/pages/56098818';

export const appVersion: AppVersionInfo = {
  version,
  sha,
  branch,
  buildTime,
  env,
  label,
};
